import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppear } from '../hooks/useAppear';
import { useCountUp } from '../hooks/useCountUp';
import Img from '../components/ui/Img';
import { testimonials, partners, programmes } from '../data';
import {
  IcoArrow, IcoBook, IcoActivity, IcoUsers,
  IcoShield, IcoHome, IcoTarget, IcoLeaf,
  IcoHeart, IcoAward, IcoQuote,
} from '../assets/icons';

/* ── prog icon map ─── */
const progIcons = {
  pdic:          IcoHome,
  tata:          IcoActivity,
  geracao:       IcoShield,
  'seja-mulher': IcoUsers,
  dikota:        IcoHeart,
  campus:        IcoBook,
  revieng:       IcoLeaf,
  'pe-boto':     IcoAward,
};

/* ═══════════════════════════════════════════════════════════
   LAZY GDRIVE VIDEO
   • Só carrega quando visível (Intersection Observer)
   • Pausa automaticamente ao sair do viewport — recarrega o
     src com autoplay=0 para forçar stop no iframe sandboxado
   • Cada instância é completamente isolada
   ═══════════════════════════════════════════════════════════ */
function LazyGDriveVideo({ src, title, className, style }) {
  const containerRef = useRef(null);
  const [phase, setPhase]   = useState('hidden');   // hidden | loading | playing | paused
  const [iframeSrc, setIframeSrc] = useState('');

  const fileId = src?.match(/\/d\/([^/]+)/)?.[1] ?? '';
  const baseUrl = fileId ? `https://drive.google.com/file/d/${fileId}/preview` : '';

  // Gera um src único com timestamp para forçar reload (pausa)
  const buildSrc = useCallback((play) =>
    `${baseUrl}?autoplay=${play ? 1 : 0}&t=${Date.now()}`, [baseUrl]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Entrou no viewport → carrega (não autoplay ainda)
          setPhase(p => p === 'hidden' ? 'loading' : p);
        } else {
          // Saiu do viewport → pausa recarregando sem autoplay
          setPhase(p => {
            if (p === 'playing' || p === 'loading') {
              setIframeSrc(buildSrc(false));
              return 'paused';
            }
            return p;
          });
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [buildSrc]);

  // Quando muda para loading, define o src inicial
  useEffect(() => {
    if (phase === 'loading') {
      setIframeSrc(buildSrc(false));
    }
  }, [phase, buildSrc]);

  const handlePlay = () => {
    setIframeSrc(buildSrc(false)); // drive não aceita autoplay externo; abre o player
    setPhase('playing');
  };

  const isReady = phase !== 'hidden';

  return (
    <div ref={containerRef} className={className} style={style}>
      {/* ── Placeholder ── */}
      {!isReady && (
        <div className="w-full h-full bg-brand-900 rounded-2xl flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-brand-500">
            <svg className="w-12 h-12 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" strokeWidth="1.5"/>
              <path d="M10 8l6 4-6 4V8z" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
            <span className="text-xs uppercase tracking-widest opacity-40">{title}</span>
          </div>
        </div>
      )}

      {/* ── Player ── */}
      {isReady && (
        <div className="relative w-full h-full">
          {/* Spinner enquanto carrega */}
          {phase === 'loading' && (
            <div className="absolute inset-0 bg-brand-900 rounded-2xl flex items-center justify-center z-10">
              <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          <iframe
            key={iframeSrc}
            src={iframeSrc}
            title={title}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            onLoad={() => setPhase(p => p === 'loading' ? 'playing' : p)}
            className="w-full h-full rounded-2xl border-0 block"
          />
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   VIDEO SHOWCASE — efeito cinematográfico
   Inspirado na referência: overlay escuro, setas, thumbnails
   laterais, badge de categoria, título sobreposto
   ═══════════════════════════════════════════════════════════ */
const SHOWCASE_VIDEOS = [
  {
    id:    '1jOCjTVXPzbt--ZC5HyP_1jDnyK3rcPOt',
    thumb: 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9449_1.JPG',
    tag_pt: 'Acções no Terreno',
    tag_en: 'Field Actions',
    title_pt: 'Impacto Real nas Comunidades',
    title_en: 'Real Impact in Communities',
    sub_pt: 'Fundação Ngana Zenza em acção nas províncias de Angola',
    sub_en: 'Ngana Zenza Foundation in action across Angola\'s provinces',
  },
  {
    id:    '1XiZtmOL6n30bWcCPjRnpSU6oNzA-Z1Y-',
    thumb: 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/01.jpg',
    tag_pt: 'Programa TATA',
    tag_en: 'TATA Programme',
    title_pt: 'Saúde e Nutrição Infantil',
    title_en: 'Child Health & Nutrition',
    sub_pt: 'Levando cuidados de saúde às famílias mais vulneráveis',
    sub_en: 'Bringing healthcare to the most vulnerable families',
  },
  {
    id:    '1X6PSqu4yFAXwNSD56hdAtzK2QN45qmxX',
    thumb: 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/DD.jpg',
    tag_pt: 'Vídeo Institucional',
    tag_en: 'Institutional Video',
    title_pt: 'A Nossa Missão em Angola',
    title_en: 'Our Mission in Angola',
    sub_pt: 'Desenvolvimento humano sustentável para todas as comunidades',
    sub_en: 'Sustainable human development for every community',
  },
  {
    id:    '1A7sa8eMcaChOaXx7cNZs1n7chPPWOOGU',
    thumb: 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9090.JPG',
    tag_pt: 'Programa PDIC',
    tag_en: 'PDIC Programme',
    title_pt: 'Desenvolvimento Integrado Comunitário',
    title_en: 'Integrated Community Development',
    sub_pt: 'Construindo infraestruturas e capacidades nas comunidades',
    sub_en: 'Building infrastructure and capacity in communities',
  },
];

function VideoShowcase({ isPt }) {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');
  const total = SHOWCASE_VIDEOS.length;

  const cur = SHOWCASE_VIDEOS[active];
  const embedBase = `https://drive.google.com/file/d/${cur.id}/preview`;

  // Quando troca de vídeo → reset
  useEffect(() => {
    setPlaying(false);
    setIframeSrc('');
  }, [active]);

  // Auto-pause ao sair do viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setPlaying(false);
          setIframeSrc('');
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handlePlay = () => {
    setIframeSrc(`${embedBase}?autoplay=0&t=${Date.now()}`);
    setPlaying(true);
  };

  const prev = () => setActive((a) => (a - 1 + total) % total);
  const next = () => setActive((a) => (a + 1) % total);

  return (
    <section ref={sectionRef} className="section-pad bg-brand-950 overflow-hidden">
      <div className="wrap">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <p className="label-tag justify-center" style={{ color: '#ea580c' }}>
            {isPt ? 'Em Destaque' : 'Featured'}
          </p>
          <h2 className="display-md text-white mb-2">
            {isPt ? 'O Nosso Impacto em Acção' : 'Our Impact in Action'}
          </h2>
        </div>

        {/* ── SHOWCASE PRINCIPAL ── */}
        <div className="flex gap-3 lg:gap-4 items-stretch">

          {/* Seta esquerda — desktop */}
          <button
            onClick={prev}
            className="hidden lg:flex items-center justify-center w-10 flex-shrink-0 self-center
                       text-white/50 hover:text-white transition-colors"
            aria-label="Anterior"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M15 19l-7-7 7-7"/>
            </svg>
          </button>

          {/* Player principal */}
          <div className="flex-1 relative rounded-2xl overflow-hidden bg-brand-900 group"
               style={{ aspectRatio: '16/9', minHeight: '200px' }}>

            {/* Thumbnail / overlay quando não está a reproduzir */}
            {!playing && (
              <>
                <img
                  src={cur.thumb}
                  alt={isPt ? cur.title_pt : cur.title_en}
                  className="w-full h-full object-cover"
                />
                {/* Overlay escuro gradiente */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

                {/* Badge de categoria */}
                <div className="absolute top-4 left-4 sm:top-5 sm:left-5">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/20">
                    <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse flex-shrink-0" />
                    <span className="text-[10px] sm:text-[11px] font-bold text-white uppercase tracking-wider">
                      {isPt ? cur.tag_pt : cur.tag_en}
                    </span>
                  </div>
                </div>

                {/* Texto sobreposto */}
                <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 lg:p-8">
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-1.5">
                    {isPt ? cur.title_pt : cur.title_en}
                  </h3>
                  <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-lg hidden sm:block">
                    {isPt ? cur.sub_pt : cur.sub_en}
                  </p>
                </div>

                {/* Botão play central */}
                <button
                  onClick={handlePlay}
                  className="absolute inset-0 flex items-center justify-center group/play"
                  aria-label="Reproduzir"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full
                                  bg-brand-600/90 backdrop-blur-sm flex items-center justify-center
                                  shadow-2xl shadow-brand-900/60
                                  group-hover/play:bg-brand-500 group-hover/play:scale-110
                                  transition-all duration-300">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 lg:w-9 lg:h-9 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </button>

                {/* Setas mobile */}
                <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-3 lg:hidden pointer-events-none">
                  <button onClick={prev} className="pointer-events-auto w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M15 19l-7-7 7-7"/></svg>
                  </button>
                  <button onClick={next} className="pointer-events-auto w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M9 5l7 7-7 7"/></svg>
                  </button>
                </div>

                {/* Dots indicador mobile */}
                <div className="absolute bottom-3 right-4 flex gap-1.5 lg:hidden">
                  {SHOWCASE_VIDEOS.map((_, i) => (
                    <button key={i} onClick={() => setActive(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-5 bg-brand-500' : 'w-1.5 bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}

            {/* iframe quando a reproduzir */}
            {playing && (
              <iframe
                key={iframeSrc}
                src={iframeSrc}
                title={isPt ? cur.title_pt : cur.title_en}
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0 block"
              />
            )}
          </div>

          {/* Seta direita — desktop */}
          <button
            onClick={next}
            className="hidden lg:flex items-center justify-center w-10 flex-shrink-0 self-center
                       text-white/50 hover:text-white transition-colors"
            aria-label="Próximo"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          {/* Thumbnails laterais — só desktop */}
          <div className="hidden lg:flex flex-col gap-2.5 w-[160px] xl:w-[180px] flex-shrink-0 overflow-y-auto max-h-[480px] pr-0.5"
               style={{ scrollbarWidth: 'none' }}>
            {SHOWCASE_VIDEOS.map((v, i) => (
              <button
                key={v.id}
                onClick={() => setActive(i)}
                className={`relative rounded-xl overflow-hidden flex-shrink-0 transition-all duration-200
                            ${i === active
                              ? 'ring-2 ring-brand-500 ring-offset-2 ring-offset-brand-950 opacity-100'
                              : 'opacity-50 hover:opacity-80'}`}
                style={{ aspectRatio: '16/10' }}
              >
                <img src={v.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                {/* Indicador de vídeo activo */}
                {i === active && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-7 h-7 rounded-full bg-brand-600/90 flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                )}
                <p className="absolute bottom-1.5 left-2 right-2 text-[10px] text-white/90 font-semibold leading-tight line-clamp-2">
                  {isPt ? v.title_pt : v.title_en}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   MOSAIC — imagens do impacto (maiores no mobile)
   ═══════════════════════════════════════════════════════════ */
const MOSAIC_IMAGES = [
  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/11.jpg.jpeg',
  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/D%20(2).jpg',
  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/DD.jpg',
  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/f.jpg',
  'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9454_1.JPG',
];

function ImpactMosaic({ isPt }) {
  const ref = useAppear();
  return (
    <section className="pb-14 sm:pb-16 bg-brand-950">
      <div className="wrap" ref={ref}>
        <p className="text-center text-[10px] font-bold text-brand-600 uppercase tracking-[0.18em] mb-5">
          {isPt ? 'Galeria de Impacto' : 'Impact Gallery'}
        </p>
        {/* Mobile: 2 colunas com alturas maiores | Desktop: grid livre */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
          {/* Imagem destaque — ocupa 2 colunas no mobile, 1 no desktop */}
          <div className="col-span-2 lg:col-span-1 lg:row-span-2 rounded-2xl overflow-hidden"
               style={{ height: 'clamp(220px, 55vw, 420px)' }}>
            <img src={MOSAIC_IMAGES[0]} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
          </div>
          {MOSAIC_IMAGES.slice(1).map((img, i) => (
            <div key={i} className="rounded-xl overflow-hidden"
                 style={{ height: 'clamp(140px, 32vw, 200px)' }}>
              <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   HERO
   ═══════════════════════════════════════════════════════════ */
const HERO_BG_IMAGE  = 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9449_1.JPG';
const HERO_CHILD_IMG = 'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9067.JPG';

function Hero({ onNavigate, isPt }) {
  const textRef = useRef(null);
  const imgRef  = useRef(null);

  useEffect(() => {
    const run = (el, delay, y) => {
      if (!el) return;
      el.style.transition = `opacity 0.9s ease ${delay}ms, transform 0.9s ease ${delay}ms`;
      setTimeout(() => { el.style.opacity = '1'; el.style.transform = 'translate(0,0)'; }, 60);
    };
    run(textRef.current, 0,   '24px');
    run(imgRef.current,  120, '-24px');
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-[68px]">
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG_IMAGE} alt="" className="w-full h-full object-cover" style={{ opacity: 0.07 }} loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/99 dark:from-brand-950/99 via-white/96 dark:via-brand-950/96 to-white/88 dark:to-brand-950/88" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/95 dark:from-brand-950/95 via-transparent to-transparent" />
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-1/2 pointer-events-none z-0 overflow-hidden hidden lg:block">
        <svg viewBox="0 0 600 800" className="w-full h-full opacity-[0.05]" preserveAspectRatio="xMidYMid slice">
          <defs><pattern id="hgeo" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M30 4 L56 56 L4 56 Z" fill="none" stroke="#ea580c" strokeWidth="0.7"/>
          </pattern></defs>
          <rect width="600" height="800" fill="url(#hgeo)"/>
        </svg>
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-400/10 blur-[100px]" />
      </div>

      <div className="wrap relative z-10 py-16 sm:py-20 lg:py-28 w-full">
        <div className="flex flex-col-reverse lg:flex-row items-center gap-10 lg:gap-16">

          {/* Imagem criança */}
          <div ref={imgRef} style={{ opacity: 0, transform: 'translateX(-24px)' }}
               className="w-full lg:w-[38%] flex-shrink-0">
            <div className="relative mx-auto w-[260px] sm:w-[300px] lg:w-full max-w-[380px]">
              <div className="absolute -bottom-4 -right-4 w-full h-full rounded-3xl bg-brand-600/20 dark:bg-brand-600/15" />
              <div className="absolute -top-4 -left-4 w-16 h-16 rounded-2xl bg-brand-100 dark:bg-brand-900/60" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-brand-900/20" style={{ aspectRatio: '3/4' }}>
                <img src={HERO_CHILD_IMG} alt={isPt ? 'Criança apoiada pela Fundação Ngana Zenza' : 'Child supported by the Ngana Zenza Foundation'} className="w-full h-full object-cover" loading="eager" />
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-brand-900/50 to-transparent" />
              </div>
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-brand-900 shadow-lg shadow-brand-900/15 border border-brand-100 dark:border-brand-800">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                <span className="text-[11px] font-bold text-brand-700 dark:text-brand-300 uppercase tracking-wide">
                  {isPt ? '50+ Comunidades Apoiadas' : '50+ Communities Supported'}
                </span>
              </div>
            </div>
          </div>

          {/* Texto */}
          <div ref={textRef} style={{ opacity: 0, transform: 'translateY(24px)' }} className="flex-1 text-center lg:text-left">
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-brand-200 dark:border-brand-800 bg-brand-50/80 dark:bg-brand-900/40 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-600 animate-pulse" />
              <span className="text-[11px] font-bold text-brand-600 dark:text-brand-400 uppercase tracking-[0.14em]">
                {isPt ? 'Angola · Fundada em 2020' : 'Angola · Founded 2020'}
              </span>
            </div>
            <h1 className="display-xl text-brand-900 dark:text-white mb-6 leading-[1.1]">
              {isPt ? (<>Construímos<br /><span className="grad-text">Futuros.</span>{' '}Transformamos<br /><span className="grad-text">Angola.</span></>) : (<>We Build<br /><span className="grad-text">Futures.</span>{' '}We Transform<br /><span className="grad-text">Angola.</span></>)}
            </h1>
            <p className="body-lg text-brand-700 dark:text-brand-400 max-w-lg mb-8 mx-auto lg:mx-0">
              {isPt ? 'A Fundação Ngana Zenza para o Desenvolvimento Comunitário promove o desenvolvimento humano sustentável nas comunidades mais vulneráveis de Angola.' : 'The Ngana Zenza Foundation for Community Development promotes sustainable human development in Angola\'s most vulnerable communities.'}
            </p>
            <div className="flex items-center justify-center lg:justify-start gap-6 mb-10">
              {[{ n:'8', l: isPt?'Programas':'Programmes'},{n:'10+',l:isPt?'Províncias':'Provinces'},{n:'5',l:isPt?'Anos':'Years'}].map((m,i)=>(
                <div key={i} className={`text-center ${i>0?'pl-6 border-l border-brand-200 dark:border-brand-800':''}`}>
                  <div className="text-2xl font-bold text-brand-600">{m.n}</div>
                  <div className="text-[11px] text-brand-500 font-medium mt-0.5">{m.l}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <button onClick={() => onNavigate('/programas')} className="btn-primary px-7 py-3.5 text-[14px]">
                {isPt ? 'Os Nossos Programas' : 'Our Programmes'}<IcoArrow cls="w-4 h-4" />
              </button>
              <button onClick={() => onNavigate('/quem-somos')} className="btn-outline px-7 py-3.5 text-[14px]">
                {isPt ? 'Quem Somos' : 'About Us'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1">
        <div className="w-[22px] h-[36px] rounded-full border-2 border-brand-300 dark:border-brand-700 flex items-start justify-center pt-1.5">
          <div className="w-1 h-2.5 rounded-full bg-brand-600 animate-bounce" />
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   FOUNDER QUOTE
   ═══════════════════════════════════════════════════════════ */
function FounderQuote({ isPt }) {
  const ref = useAppear();
  return (
    <section className="bg-brand-600 py-14 sm:py-16">
      <div className="wrap">
        <div ref={ref} className="flex flex-col lg:flex-row items-center gap-8 lg:gap-10 max-w-4xl mx-auto">
          <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-white/30">
            <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/24.jpg" label="Ana Dias Lourenço" className="w-full h-full" />
          </div>
          <div className="flex-1 text-center lg:text-left">
            <IcoQuote cls="w-7 h-7 sm:w-8 sm:h-8 text-white/30 mb-3 mx-auto lg:mx-0" />
            <p className="text-white text-base sm:text-lg lg:text-xl font-light leading-relaxed italic mb-5">
              {isPt ? '"A Fundação Ngana Zenza nasceu do desejo profundo de contribuir para que nenhum angolano fique para trás — cada comunidade merece dignidade, saúde e oportunidade."' : '"The Ngana Zenza Foundation was born from a deep desire to ensure no Angolan is left behind — every community deserves dignity, health and opportunity."'}
            </p>
            <p className="text-white font-bold text-[15px]">Ana Dias Lourenço</p>
            <p className="text-brand-200 text-[11px] uppercase tracking-wider mt-0.5">
              {isPt ? 'Fundadora · Primeira-Dama da República de Angola' : 'Founder · First Lady of the Republic of Angola'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   STATS BAR
   ═══════════════════════════════════════════════════════════ */
function StatCard({ label, value, suffix = '' }) {
  const { count, ref } = useCountUp(value, 2200);
  const display = value >= 1000 ? Math.round(count / 1000) + 'K' : count;
  return (
    <div ref={ref} className="text-center py-7 sm:py-8 px-3 sm:px-4">
      <div className="text-3xl sm:text-4xl font-bold text-brand-600 mb-1.5">{display}{suffix}</div>
      <div className="text-xs sm:text-sm text-brand-700 dark:text-brand-500 font-medium leading-snug">{label}</div>
    </div>
  );
}
function StatsBar({ isPt }) {
  const stats = [
    { label: isPt ? 'Programas Activos'    : 'Active Programmes',  value: 8,  suffix: '' },
    { label: isPt ? 'Províncias Alcançadas': 'Provinces Reached',  value: 10, suffix: '+' },
    { label: isPt ? 'Comunidades'          : 'Communities',        value: 50, suffix: '+' },
    { label: isPt ? 'Anos de Impacto'      : 'Years of Impact',    value: 5,  suffix: '' },
  ];
  return (
    <section className="border-y border-brand-100 dark:border-brand-900 bg-white dark:bg-brand-950">
      <div className="wrap grid grid-cols-2 lg:grid-cols-4 divide-x divide-brand-100 dark:divide-brand-900">
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   ABOUT STRIP
   ═══════════════════════════════════════════════════════════ */
function AboutStrip({ isPt, onNavigate }) {
  const refL = useAppear('appear-left');
  const refR = useAppear();
  return (
    <section className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div ref={refL}>
          <p className="label-tag sec-tag">{isPt ? 'Sobre a Fundação' : 'About the Foundation'}</p>
          <h2 className="display-lg text-brand-900 dark:text-white mb-5">
            {isPt ? 'Fundada para servir\nos que mais precisam.' : 'Founded to serve\nthose who need it most.'}
          </h2>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-4">
            {isPt ? 'A Fundação Ngana Zenza para o Desenvolvimento Comunitário é uma organização filantrópica angolana criada a 20 de Fevereiro de 2020 pela Primeira-Dama da República de Angola, Ana Dias Lourenço.' : 'The Ngana Zenza Foundation for Community Development is an Angolan philanthropic organisation created on 20 February 2020 by the First Lady of the Republic of Angola, Ana Dias Lourenço.'}
          </p>
          <p className="body-md text-brand-700 dark:text-brand-400 mb-8">
            {isPt ? 'O nome "Ngana Zenza" homenageia Isabel "Ngana Zenza", mãe da Primeira-Dama — símbolo de solidariedade, humanidade e compromisso com o próximo.' : 'The name "Ngana Zenza" honours Isabel "Ngana Zenza", mother of the First Lady — a symbol of solidarity, humanity and commitment to others.'}
          </p>
          <button onClick={() => onNavigate('/quem-somos')} className="btn-primary">
            {isPt ? 'A Nossa História' : 'Our Story'}<IcoArrow cls="w-4 h-4" />
          </button>
        </div>
        <div ref={refR} className="grid grid-cols-2 gap-3">
          <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9047.JPG" alt="PDIC Comunidade" className="w-full h-52 sm:h-64 rounded-2xl" />
          <div className="space-y-3">
            <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/01.jpg" alt="Tata Uhayele" className="w-full h-[100px] sm:h-[122px] rounded-2xl" />
            <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/fotos%20pdic/_DSC9067.JPG" alt="PDIC" className="w-full h-[100px] sm:h-[122px] rounded-2xl" />
          </div>
          <div className="col-span-2">
            <Img src="https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/FOTOS%20TATA%20UHAYELE/21.jpg" alt="Tata Uhayele 21" className="w-full h-36 sm:h-40 rounded-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROGRAMMES GRID
   ═══════════════════════════════════════════════════════════ */
function ProgrammesGrid({ isPt, onNavigate }) {
  const ref = useAppear();
  return (
    <section className="section-pad bg-brand-50 dark:bg-brand-900/20">
      <div className="wrap">
        <div ref={ref} className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-12 lg:mb-14">
          <div>
            <p className="label-tag sec-tag">{isPt ? 'Programas' : 'Programmes'}</p>
            <h2 className="display-lg text-brand-900 dark:text-white">
              {isPt ? 'Oito Programas,\nUma Missão' : 'Eight Programmes,\nOne Mission'}
            </h2>
          </div>
          <p className="body-md text-brand-600 dark:text-brand-500 max-w-sm">
            {isPt ? 'Cada programa responde a uma necessidade específica das comunidades angolanas.' : 'Each programme addresses a specific need of Angolan communities.'}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {programmes.map((prog) => {
            const Icon = progIcons[prog.id] || IcoTarget;
            return (
              <button key={prog.id} onClick={() => onNavigate('/programas')} className="card-hover p-5 sm:p-6 text-left group">
                <div className="h-0.5 w-8 bg-brand-600 mb-4 sm:mb-5 group-hover:w-full transition-all duration-300" />
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl border border-brand-100 dark:border-brand-900 flex items-center justify-center text-brand-600 dark:text-brand-500 mb-3 sm:mb-4 group-hover:bg-brand-600 group-hover:text-white group-hover:border-brand-600 transition-all duration-200">
                  <Icon cls="w-5 h-5" />
                </div>
                <div className="text-[10px] font-bold text-brand-500 uppercase tracking-widest mb-1.5">{prog.acronym}</div>
                <h3 className="text-[13px] sm:text-[14px] font-bold text-brand-900 dark:text-white leading-snug mb-2">{isPt ? prog.title_pt : prog.title_en}</h3>
                <p className="text-[11px] sm:text-[12px] text-brand-600 dark:text-brand-600 leading-relaxed line-clamp-2">{isPt ? prog.subtitle_pt : prog.subtitle_en}</p>
              </button>
            );
          })}
        </div>
        <div className="text-center mt-8 sm:mt-10">
          <button onClick={() => onNavigate('/programas')} className="btn-outline">
            {isPt ? 'Ver Todos os Programas' : 'View All Programmes'}<IcoArrow cls="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   VÍDEO INSTITUCIONAL — efeito cinematográfico idêntico ao
   VideoShowcase: thumbnail com overlay, badge, título, play
   Player customizado — sem UI do Google Drive
   ═══════════════════════════════════════════════════════════ */
const INST_VIDEO = {
  id:      '1gLFqxBmceWX4I1FQET91hIEsog4UnIg5',
  thumb:   'https://jpumokwyaqpqmqubhaep.supabase.co/storage/v1/object/public/franstel_armazenamento/img_fdc/OIF.jpg',
  tag_pt:  'Vídeo Institucional',
  tag_en:  'Institutional Video',
  title_pt:'O Nosso Trabalho em Acção',
  title_en:'Our Work in Action',
  sub_pt:  'Veja o impacto real da Fundação Ngana Zenza nas comunidades angolanas.',
  sub_en:  'See the real impact of the Ngana Zenza Foundation on Angolan communities.',
};

function VideoSection({ isPt }) {
  const sectionRef = useRef(null);
  const headerRef  = useAppear();
  const [playing,   setPlaying]   = useState(false);
  const [iframeSrc, setIframeSrc] = useState('');

  const embedBase = `https://drive.google.com/file/d/${INST_VIDEO.id}/preview`;

  // Auto-pause ao sair do viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          setPlaying(false);
          setIframeSrc('');
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handlePlay = () => {
    setIframeSrc(`${embedBase}?autoplay=0&t=${Date.now()}`);
    setPlaying(true);
  };

  return (
    <section ref={sectionRef} className="section-pad bg-white dark:bg-brand-950">
      <div className="wrap">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-10 sm:mb-12">
          <p className="label-tag justify-center sec-tag">
            {isPt ? INST_VIDEO.tag_pt : INST_VIDEO.tag_en}
          </p>
          <h2 className="display-md text-brand-900 dark:text-white mb-4">
            {isPt ? INST_VIDEO.title_pt : INST_VIDEO.title_en}
          </h2>
          <p className="body-md text-brand-600 dark:text-brand-500 max-w-lg mx-auto">
            {isPt ? INST_VIDEO.sub_pt : INST_VIDEO.sub_en}
          </p>
        </div>

        {/* ── Player cinematográfico ── */}
        <div className="relative max-w-4xl mx-auto rounded-2xl overflow-hidden bg-brand-900"
             style={{ aspectRatio: '16/9', minHeight: '200px' }}>

          {/* ── Estado: thumbnail com overlay ── */}
          {!playing && (
            <>
              <img
                src={INST_VIDEO.thumb}
                alt={isPt ? INST_VIDEO.title_pt : INST_VIDEO.title_en}
                className="w-full h-full object-cover"
              />
              {/* Overlays escuros */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/55 to-transparent" />

              {/* Badge de categoria */}
              <div className="absolute top-4 left-4 sm:top-6 sm:left-6">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/40 backdrop-blur-sm border border-white/20">
                  <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse flex-shrink-0" />
                  <span className="text-[10px] sm:text-[11px] font-bold text-white uppercase tracking-wider">
                    {isPt ? INST_VIDEO.tag_pt : INST_VIDEO.tag_en}
                  </span>
                </div>
              </div>

              {/* Texto sobreposto em baixo */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7 lg:p-9">
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-tight mb-1.5">
                  {isPt ? INST_VIDEO.title_pt : INST_VIDEO.title_en}
                </h3>
                <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-xl hidden sm:block">
                  {isPt ? INST_VIDEO.sub_pt : INST_VIDEO.sub_en}
                </p>
              </div>

              {/* Botão play central */}
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center group/play"
                aria-label={isPt ? 'Reproduzir vídeo' : 'Play video'}
              >
                {/* Anel pulsante decorativo */}
                <div className="absolute w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-full border-2 border-white/20 animate-ping opacity-30" />
                <div className="relative w-14 h-14 sm:w-18 sm:h-18 lg:w-22 lg:h-22 rounded-full
                                bg-brand-600/90 backdrop-blur-sm flex items-center justify-center
                                shadow-2xl shadow-brand-900/60
                                group-hover/play:bg-brand-500 group-hover/play:scale-110
                                transition-all duration-300"
                     style={{ width: 'clamp(56px,8vw,80px)', height: 'clamp(56px,8vw,80px)' }}>
                  <svg className="text-white ml-1"
                       style={{ width: 'clamp(22px,3vw,34px)', height: 'clamp(22px,3vw,34px)' }}
                       fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
              </button>

              {/* Duração / qualidade badge — canto inferior direito */}
              <div className="absolute bottom-4 right-4 hidden sm:flex items-center gap-1.5
                              px-2.5 py-1 rounded-md bg-black/50 backdrop-blur-sm border border-white/10">
                <svg className="w-3 h-3 text-brand-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
                </svg>
                <span className="text-[10px] font-bold text-white/80 uppercase tracking-wider">HD</span>
              </div>
            </>
          )}

          {/* ── Estado: iframe a reproduzir ── */}
          {playing && (
            <iframe
              key={iframeSrc}
              src={iframeSrc}
              title={isPt ? INST_VIDEO.title_pt : INST_VIDEO.title_en}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full border-0 block"
            />
          )}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   TESTIMONIALS
   ═══════════════════════════════════════════════════════════ */
function Testimonials({ isPt }) {
  const [active, setActive] = useState(0);
  const ref = useAppear();
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % testimonials.length), 5500);
    return () => clearInterval(id);
  }, []);
  const t = testimonials[active];
  return (
    <section className="section-pad bg-brand-50 dark:bg-brand-900/20">
      <div className="wrap">
        <div ref={ref} className="text-center mb-12 sm:mb-14">
          <p className="label-tag justify-center sec-tag">{isPt ? 'Testemunhos' : 'Testimonials'}</p>
          <h2 className="display-md text-brand-900 dark:text-white">{isPt ? 'Vozes das Comunidades' : 'Community Voices'}</h2>
        </div>
        <div className="max-w-2xl mx-auto">
          <div className="card-base p-7 sm:p-10">
            <IcoQuote cls="w-8 h-8 sm:w-10 sm:h-10 text-brand-200 dark:text-brand-800 mb-4 sm:mb-5" />
            <p className="text-lg sm:text-xl text-brand-800 dark:text-brand-200 leading-relaxed font-light mb-7 sm:mb-8">{isPt ? t.text_pt : t.text_en}</p>
            <div className="flex items-center gap-4 pt-5 sm:pt-6 border-t border-brand-100 dark:border-brand-900">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-600 flex items-center justify-center text-white font-bold text-base flex-shrink-0">{t.name[0]}</div>
              <div>
                <p className="font-semibold text-brand-900 dark:text-white text-sm">{t.name}</p>
                <p className="text-brand-500 text-xs mt-0.5">{isPt ? t.location_pt : t.location_en}</p>
              </div>
            </div>
          </div>
          <div className="flex justify-center gap-2 mt-5 sm:mt-6">
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setActive(i)}
                className={`h-2 rounded-full transition-all duration-300 ${i === active ? 'w-8 bg-brand-600' : 'w-2 bg-brand-200 dark:bg-brand-800 hover:bg-brand-400'}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   PARTNERS BAR
   ═══════════════════════════════════════════════════════════ */
function PartnersBar({ isPt }) {
  return (
    <section className="py-12 sm:py-14 border-y border-brand-100 dark:border-brand-900 bg-white dark:bg-brand-950">
      <div className="wrap">
        <p className="text-center text-[10px] font-bold text-brand-400 uppercase tracking-[0.18em] mb-6 sm:mb-8">
          {isPt ? 'Parceiros e Entidades Apoiantes' : 'Partners & Supporting Entities'}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {partners.map((p, i) => (
            <div key={i} className="px-4 sm:px-5 py-2 rounded-lg border border-brand-100 dark:border-brand-900 text-brand-600 dark:text-brand-600 font-bold text-[12px] sm:text-[13px] hover:border-brand-400 hover:text-brand-700 dark:hover:text-brand-400 transition-all cursor-default">{p.abbr}</div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   DONATE CTA
   ═══════════════════════════════════════════════════════════ */
function DonateCTA({ isPt, onNavigate }) {
  const ref = useAppear();
  return (
    <section className="relative overflow-hidden bg-brand-600 py-20 sm:py-24">
      <div className="absolute inset-0 opacity-[0.06]">
        <svg width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
          <defs><pattern id="ctapat" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M24 4 L44 44 L4 44 Z" fill="none" stroke="white" strokeWidth="0.8"/>
          </pattern></defs>
          <rect width="100%" height="100%" fill="url(#ctapat)"/>
        </svg>
      </div>
      <div className="absolute right-0 top-0 w-[400px] sm:w-[500px] h-[400px] sm:h-[500px] rounded-full bg-brand-700/40 blur-[120px] translate-x-1/2 -translate-y-1/4" />
      <div ref={ref} className="wrap relative z-10 text-center max-w-2xl mx-auto px-4">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-6 sm:mb-7">
          <IcoHeart cls="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </div>
        <h2 className="display-lg text-white mb-4 sm:mb-5">
          {isPt ? 'O Seu Apoio\nTransforma Vidas' : 'Your Support\nTransforms Lives'}
        </h2>
        <p className="text-brand-100 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed">
          {isPt ? 'Junte-se à missão da Fundação Ngana Zenza e contribua para o desenvolvimento sustentável de Angola.' : 'Join the Ngana Zenza Foundation\'s mission and contribute to Angola\'s sustainable development.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          <button onClick={() => onNavigate('/contacto')} className="btn-white px-7 sm:px-8 py-3.5 sm:py-4 text-[14px]">
            {isPt ? 'Apoiar a Fundação' : 'Support the Foundation'}<IcoArrow cls="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════ */
export default function HomePage({ onNavigate }) {
  const { i18n } = useTranslation();
  const isPt = i18n.language === 'pt';
  return (
    <main>
      <Hero            onNavigate={onNavigate} isPt={isPt} />
      <VideoShowcase   isPt={isPt} />
      <ImpactMosaic    isPt={isPt} />
      <FounderQuote    isPt={isPt} />
      <StatsBar        isPt={isPt} />
      <AboutStrip      isPt={isPt} onNavigate={onNavigate} />
      <ProgrammesGrid  isPt={isPt} onNavigate={onNavigate} />
      <VideoSection    isPt={isPt} />
      <Testimonials    isPt={isPt} />
    
      <DonateCTA       isPt={isPt} onNavigate={onNavigate} />
    </main>
  );
}